import Mousetrap from 'mousetrap';
import * as RA from 'ramda-adjunct';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import useEventCallback from 'common/lib/hooks/useEventCallback';
import noop from 'common/utils/noop';
import type {
  MonarchCommand,
  MonarchCommandGroup,
  MonarchEntityCommand,
  MonarchKnownCommand,
} from 'lib/commands/types';
import isEnvDevelopment from 'lib/isEnvDevelopment';
import { ShortcutType } from 'lib/keyboardShortcuts';

import type { ConditionalReturnType } from 'common/types/utility';

const VERBOSE = isEnvDevelopment();

type Sequence = string;
type CommandId = MonarchKnownCommand['id'];

type CommandContextType = {
  /** The commands in the registry. */
  commands: Map<CommandId, MonarchKnownCommand>;
  /** The sequences in the registry. */
  sequences: Map<Sequence, CommandId>;
  /** Whether shortcuts are enabled. They are usually disabled when the command palette is open. */
  shortcutsEnabled: boolean;
  /** Adds a command to the registry. */
  registerCommand: (command: MonarchKnownCommand, group?: MonarchCommandGroup) => () => void;
  /** Unregisters a command from the registry. */
  unregisterCommand: (id: CommandId) => void;
  /** Execute a sequence of commands. */
  executeSequence: (sequence: string[]) => boolean;
  /** Get all commands in a group. */
  getCommands: (group?: MonarchCommandGroup) => MonarchKnownCommand[];
  /** Get a command by its ID. */
  getCommand: (id: CommandId) => MonarchKnownCommand | undefined;
  /** Whether the command palette component is open. */
  isOpen: boolean;
  /** Toggle the command palette component. */
  toggleCommandPalette: () => void;
};

const CommandContext = createContext<CommandContextType | null>(null);

/**
 * ### Command Provider Component
 *
 * This provider is designed to be placed at the root of the application for several reasons:
 *
 * 1. Commands need to be accessible from anywhere in the app.
 * 2. Maintains a single source of truth for all commands.
 * 3. Only components that consume this context will re-render on changes (not the entire app tree).
 * 4. Values are memoized to prevent unnecessary re-renders.
 */
export const CommandPaletteProvider = ({ children }: React.PropsWithChildren) => {
  const [isOpen, setIsOpen] = useState(false);
  const [commands, setCommands] = useState<Map<CommandId, MonarchKnownCommand>>(new Map());
  const [sequences, setSequences] = useState<Map<Sequence, CommandId>>(new Map());
  const [shortcutsEnabled, setShortcutsEnabled] = useState(true);

  const toggleCommandPalette = useCallback(
    () =>
      setIsOpen((prev) => {
        const nextIsOpen = !prev;

        if (nextIsOpen === false) {
          setShortcutsEnabled(true);
        } else {
          setShortcutsEnabled(false);
        }

        return nextIsOpen;
      }),
    [],
  );

  const unregisterCommand = useCallback((id: CommandId) => {
    setCommands((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });

    verboseLog(`Command ${id} unregistered.`);
  }, []);

  /**
   * Register a sequence that triggers a command.
   *
   * Sequences are different from shortcuts because they are triggered by a sequence
   * of keys within a threshold of time (see `useSequenceShortcuts`).
   */
  const registerSequence = useCallback((sequence: string[], commandId: string) => {
    // NOTE: Maps don't like arrays as keys, perhaps because each array is a different
    // reference, so we join the sequence into a string to use it as a key.
    const sequenceString = sequence.join(' ');

    // Add the sequence to the registry
    setSequences((prev) => {
      const next = new Map(prev);
      next.set(sequenceString, commandId);
      return next;
    });

    // Return a cleanup function to unbind the sequence
    return () => {
      setSequences((prev) => {
        const next = new Map(prev);
        next.delete(sequenceString);
        return next;
      });
    };
  }, []);

  const registerCommand = useEventCallback(
    (command: MonarchKnownCommand, group?: MonarchCommandGroup) => {
      setCommands((prevCommands) => {
        // Check if command is already registered using the current state
        if (prevCommands.has(command.id)) {
          verboseLog(`Command ${command.id} already registered. Not registering again.`);
          return prevCommands;
        }

        const next = new Map(prevCommands);
        const finalGroup: MonarchCommandGroup = group ?? command.group ?? 'miscellaneous';
        next.set(command.id, { ...command, group: finalGroup });
        verboseLog(`Command ${command.id} registered.`);
        return next;
      });

      // Bind the command to the keyboard, if keys are provided
      let maybeUnbindKeyboardEvent: (() => void) | undefined;
      if (command.keys) {
        if (command.shortcutType === ShortcutType.SEQUENCE) {
          maybeUnbindKeyboardEvent = registerSequence(command.keys, command.id);
        } else {
          const bind = Mousetrap.bind(command.keys, (e, combo) => {
            if (!shortcutsEnabled) {
              verboseLog('Shortcuts are disabled.');
              return;
            }

            verboseLog(`Command ${command.id} triggered.`);
            command.action({ combo });
          });

          maybeUnbindKeyboardEvent = () => {
            // Doesn't make much sense but just to make TS happy
            if (command.keys) {
              bind.unbind(command.keys);
            }
          };
        }
      }

      return () => {
        maybeUnbindKeyboardEvent?.();
        unregisterCommand(command.id);
      };
    },
  );

  const executeSequence = useCallback(
    (sequence: string[]): boolean => {
      const sequenceString = sequence.join(' ');

      const commandId = sequences.get(sequenceString);
      if (!commandId) {
        return false;
      }

      const command = commands.get(commandId);
      if (!command) {
        return false;
      }

      command.action({ combo: sequenceString });
      return true;
    },
    [commands, sequences],
  );

  const getCommands = useCallback(
    (group?: MonarchCommandGroup): MonarchKnownCommand[] => {
      if (group) {
        return Array.from(commands.values()).filter((command) => command.group === group);
      }

      return Array.from(commands.values());
    },
    [commands],
  );

  const getCommand = useCallback(
    (id: CommandId): MonarchKnownCommand | undefined => commands.get(id),
    [commands],
  );

  const contextValue = useMemo(
    () => ({
      commands,
      sequences,
      shortcutsEnabled,
      registerCommand,
      unregisterCommand,
      executeSequence,
      getCommands,
      getCommand,
      isOpen,
      toggleCommandPalette,
    }),
    [
      commands,
      sequences,
      shortcutsEnabled,
      registerCommand,
      unregisterCommand,
      executeSequence,
      getCommands,
      getCommand,
      isOpen,
      toggleCommandPalette,
    ],
  );

  return <CommandContext.Provider value={contextValue}>{children}</CommandContext.Provider>;
};

// Hook to access the command registry
export const useCommandRegistry = () => {
  const context = useContext(CommandContext);
  if (!context) {
    throw new Error('useCommandRegistry must be used within a <CommandPaletteProvider>');
  }
  return context;
};

/**
 * Utility hook to get a command by group
 */
export const useGetCommandsByGroup = <T extends MonarchCommandGroup>(group: T) => {
  const { getCommands } = useCommandRegistry();

  return getCommands(group) as ConditionalReturnType<
    MonarchEntityCommand,
    MonarchCommand,
    T extends 'entities' ? true : false
  >[];
};

/**
 * Utility hook to register commands that are context-specific.
 *
 * This is useful for registering commands that are only available in certain contexts,
 * such as when the command palette is open or when a specific route is active.
 *
 * **It automatically unregisters the commands when the component that uses this hook unmounts.**
 *
 * IMPORTANT: Remember to memoize the commands array if it's not already memoized. Otherwise,
 *            the commands will be unregistered and registered on every render as the commands array
 *            is a new reference on every render. We can probably improve this down the line.
 */
export const useRegisterContextualCommands = (
  /** The commands to register. */
  commands: MonarchKnownCommand[],
  /** Additional, optional group-wide condition that must be met for the commands to be registered. */
  shouldRegister?: boolean,
) => {
  const { registerCommand } = useCommandRegistry();

  useEffect(() => {
    let unregisterCommands: (() => void)[] = [];

    if (RA.isNotNil(shouldRegister) ? !!shouldRegister : true) {
      unregisterCommands = commands.map((command) => {
        // Also check if the command has a condition and if it doesn't, register it
        if (Object.hasOwn(command, 'shouldRegister') && !command.shouldRegister) {
          return noop;
        }

        return registerCommand(command);
      });
    }

    return () => {
      unregisterCommands.forEach((unregister) => unregister());
    };
  }, [shouldRegister, commands, registerCommand]);
};

/**
 * Log a message if verbose logging is enabled.
 *
 * @param message {string} - The message to log.
 */
export const verboseLog = (message: string) => {
  if (VERBOSE) {
    // eslint-disable-next-line no-console
    console.log(message);
  }
};
